import axios from 'axios';
import { handleUnauthorized } from './apiClient';

let installed = false;

function isAuthEndpoint(url: string): boolean {
  const lower = url.toLowerCase();
  return lower.includes('/api/account/signin') || lower.includes('/api/account/signup');
}

function hasAuthorizationHeader(headers: HeadersInit | undefined): boolean {
  if (!headers) return false;
  const h = new Headers(headers);
  return !!h.get('Authorization');
}

function hasAnySessionToken(): boolean {
  return !!(
    localStorage.getItem('authToken') ||
    localStorage.getItem('toolboxToken') ||
    localStorage.getItem('amsToken') ||
    localStorage.getItem('refreshToken')
  );
}

/**
 * Endpoints whose 401 says nothing about the Hub session.
 *
 * Every call to the user-service origin (userapi) authenticates with
 * toolboxToken or amsToken - separate, secondary tokens exchanged after
 * login - never the Hub session JWT. A session that never completed that
 * exchange (or whose secondary token went stale) 401s here even though the
 * Hub login is perfectly valid. Treating it as session expiry logs the user
 * out over a service they were never provisioned for - which is exactly what
 * happened on the Marketplace page's usercount call.
 */
const OPTIONAL_401_ENDPOINTS = ['userapi'];

function isOptionalEndpoint(url: string): boolean {
  const lower = url.toLowerCase();
  return OPTIONAL_401_ENDPOINTS.some(fragment => lower.includes(fragment));
}

function shouldHandle401(url: string, authHeaderPresent: boolean): boolean {
  if (!url || isAuthEndpoint(url) || isOptionalEndpoint(url)) return false;
  if (authHeaderPresent) return true;

  // Handle token-expiry for app API endpoints even when this specific call
  // used cookie/session/query auth instead of Authorization header.
  const isApiCall = url.toLowerCase().includes('/api/');
  if (isApiCall && hasAnySessionToken()) return true;

  return false;
}

export function installGlobalAuthInterceptors(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const requestUrl = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

    const authFromInit = hasAuthorizationHeader(init?.headers);
    const authFromRequest = input instanceof Request && input.headers.has('Authorization');
    const authHeaderPresent = authFromInit || authFromRequest;

    const response = await originalFetch(input, init);
    if (response.status === 401 && shouldHandle401(requestUrl, authHeaderPresent)) {
      handleUnauthorized();
    }
    return response;
  };

  axios.interceptors.response.use(
    response => response,
    error => {
      const status = error?.response?.status;
      if (status === 401) {
        const url: string = String(error?.config?.url ?? '');
        const authHeaderPresent = !!(error?.config?.headers?.Authorization || error?.config?.headers?.authorization);
        if (shouldHandle401(url, authHeaderPresent)) {
          handleUnauthorized();
        }
      }
      return Promise.reject(error);
    }
  );
}
